import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { PatchRequest, WebEdiDto } from '../../../data/models'
import { DespatchAdvice } from '../../../data/models/_generated'
import { requestPatchDespatchAdviceById, useDespatchAdviceById } from '../data'
import {
  DeliveryConfirmationDto,
  DeliveryConfirmationPatchDto,
} from '../data/model'
import { Attachment } from './Attachment'
import { DeliveryConfirmation } from './DeliveryConfirmation'
import { Header } from './Header'
import { OrderInformation } from './OrderInformation'
import { Position } from './Position'
import { Title } from './Title'

type Props = {
  id: WebEdiDto['Id']
}
export const DespatchAdviceData: React.FC<Props> = ({ id }) => {
  const { data, isLoading, error, mutate } = useDespatchAdviceById(id)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const dataToFormValues = (
    data?: WebEdiDto<DespatchAdvice>,
  ): DeliveryConfirmationDto => {
    return {
      deliveryNoteNumber: data?.Payload.DeliveryNoteNumber || '',
      deliveryDate: data?.Payload.DeliveryDate || '',
      deliveryType: data?.Payload.DeliveryType,
      confirmationCode: data?.Payload.ConfirmationCode,
      confirmationId: data?.Payload.ConfirmationId || '',
      confirmationDate: data?.Payload.ConfirmationDate || '',
      gln: data?.Payload.CarrierInformation?.GLN || '',
      company: data?.Payload.CarrierInformation?.Company || '',
      department: data?.Payload.SellerInformation.Department || '',
      name: data?.Payload.SellerInformation.Contact?.Name || '',
      phone: data?.Payload.SellerInformation.Contact?.Phone || '',
      email: data?.Payload.SellerInformation.Contact?.Email || '',
      notes: data?.Payload.Notes || '',
    }
  }

  const patchFromFormValues = (
    formData: DeliveryConfirmationPatchDto,
  ): PatchRequest<WebEdiDto<DespatchAdvice>> => {
    const patchOperations: PatchRequest<WebEdiDto<DespatchAdvice>> = []

    // deliveryNoteNumber
    if (formData.deliveryNoteNumber !== data!.Payload.DeliveryNoteNumber) {
      patchOperations.push({
        op: data!.Payload.DeliveryNoteNumber ? 'replace' : 'add',
        path: '/Payload/DeliveryNoteNumber',
        value: formData.deliveryNoteNumber,
      })
    }

    // deliveryDate
    if (formData.deliveryDate !== data!.Payload.DeliveryDate) {
      patchOperations.push({
        op: data!.Payload.DeliveryDate ? 'replace' : 'add',
        path: '/Payload/DeliveryDate',
        value: formData.deliveryDate,
      })
    }

    // gln
    if (formData.gln !== data!.Payload.CarrierInformation?.GLN) {
      patchOperations.push({
        op: data!.Payload.CarrierInformation?.GLN ? 'replace' : 'add',
        path: '/Payload/CarrierInformation/GLN',
        value: formData.gln,
      })
    }

    // company
    if (formData.company !== data!.Payload.CarrierInformation?.Company) {
      patchOperations.push({
        op: data!.Payload.CarrierInformation?.Company ? 'replace' : 'add',
        path: '/Payload/CarrierInformation/Company',
        value: formData.company,
      })
    }

    // department
    if (formData.department !== data!.Payload.SellerInformation.Department) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Department ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Department',
        value: formData.department,
      })
    }

    // name
    if (formData.name !== data!.Payload.SellerInformation.Contact?.Name) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Contact?.Name ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Name',
        value: formData.name,
      })
    }

    // phone
    if (formData.phone !== data!.Payload.SellerInformation.Contact?.Phone) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Contact?.Phone ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Phone',
        value: formData.phone,
      })
    }

    // email
    if (formData.email !== data!.Payload.SellerInformation.Contact?.Email) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Contact?.Email ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Email',
        value: formData.email,
      })
    }

    // notes
    if (formData.notes !== data!.Payload.Notes) {
      patchOperations.push({
        op: data!.Payload.Notes ? 'replace' : 'add',
        path: '/Payload/Notes',
        value: formData.notes,
      })
    }

    return patchOperations
  }

  const onFormSubmit = async (formData: DeliveryConfirmationPatchDto) => {
    const patchData: PatchRequest<WebEdiDto<DespatchAdvice>> =
      patchFromFormValues(formData)
    await requestPatchDespatchAdviceById(id, patchData)
    mutate()
  }

  if (error) {
    return (
      <>
        <div className="flex justify-content-between flex-wrap mb-5">
          <div className="flex justify-content-start flex-wrap">
            <div className="cursor-pointer mr-2" onClick={() => navigate('/')}>
              <i className="pi pi-angle-left text-primary text-5xl"></i>
            </div>
            <div className="font-normal text-4xl">
              {t('common.errors.noData')}
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Title id={id} orderId={data?.Payload.OrderId} isLoading={isLoading} />

      <Header id={id} />

      <OrderInformation id={id} />

      <DeliveryConfirmation
        data={dataToFormValues(data)}
        onSubmit={onFormSubmit}
        id={id}
        isLoading={isLoading}
      />

      <Position id={id} />

      <Attachment despatchAdviceId={id} processId={data?.ProcessId as string} />
    </>
  )
}
